import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Loader2, AlertCircle, ChevronRight, Menu, X,
  Phone, Mail, Building2, MapPin, FileText, Pencil, Plus,
} from "lucide-react";
import { adminApi } from "@/lib/adminApi";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";

import WorkspaceHeader from "@/pages/admin/workspace/WorkspaceHeader";
import SummaryStrip from "@/pages/admin/workspace/SummaryStrip";
import LeftColumn from "@/pages/admin/workspace/LeftColumn";
import CenterTabs from "@/pages/admin/workspace/CenterTabs";
import ActionPanel from "@/pages/admin/workspace/ActionPanel";
import BottomCards from "@/pages/admin/workspace/BottomCards";
import ProgressStepper from "@/pages/admin/workspace/ProgressStepper";

export default function Workspace() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [ws, setWs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionOpen, setActionOpen] = useState(false); // mobile action drawer

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.get(`/workspace/${caseId}`);
      setWs(data);
    } catch (err) {
      const code = err.response?.status;
      if (code === 404) setError({ kind: "not_found" });
      else setError({ kind: "other", message: err.response?.data?.detail || "Could not load workspace" });
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  // Close the mobile action drawer on Escape — keeps keyboard parity with desktop dialogs.
  useEffect(() => {
    if (!actionOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setActionOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [actionOpen]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24 text-[#475569]" data-testid="workspace-loading">
        <Loader2 className="animate-spin" size={20}/>
      </div>
    );
  }

  if (error?.kind === "not_found") {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="workspace-not-found">
        <div className="w-12 h-12 rounded-full bg-[#FCA5A5]/20 text-[#B91C1C] flex items-center justify-center mx-auto mb-4">
          <AlertCircle size={22}/>
        </div>
        <h1 className="font-display text-2xl text-[#071A33]">Job not found</h1>
        <p className="text-sm text-[#475569] mt-2">We couldn't find case <code className="text-[#0F766E]">{caseId?.slice(0, 8)}…</code> in this database.</p>
        <p className="text-xs text-[#475569] mt-2">Email links from production won't open on preview, and vice-versa.</p>
        <button onClick={() => navigate(-1)} className="btn-outline mt-6 text-sm"><ArrowLeft size={14}/> Back</button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-xl mx-auto py-20 text-center" data-testid="workspace-error">
        <p className="text-[#B91C1C]">{error.message}</p>
        <button onClick={load} className="btn-outline mt-4 text-sm">Retry</button>
      </div>
    );
  }

  const { case: c } = ws;

  return (
    <div data-testid="workspace-page" className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-4 sm:-mt-6 lg:-mt-8 -mb-4 sm:-mb-6 lg:-mb-8">
      <Helmet>
        <title>{`Job ${c.id.slice(0, 8).toUpperCase()} · ${c.full_name || "Unknown"} | Park Home Support`}</title>
      </Helmet>

      <WorkspaceHeader ws={ws} onReload={load} onBack={() => navigate(-1)} />
      <SummaryStrip ws={ws}/>

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-5 px-4 sm:px-6 lg:px-8 pt-5">
        <aside className="hidden lg:block lg:col-span-3" data-testid="workspace-left-col">
          <LeftColumn ws={ws}/>
        </aside>
        <section className="lg:col-span-6" data-testid="workspace-center-col">
          <CenterTabs ws={ws} onReload={load}/>
        </section>
        <aside className="hidden lg:block lg:col-span-3" data-testid="workspace-right-col">
          <ActionPanel ws={ws} onDone={load}/>
        </aside>

        {/* Mobile Take Action sticky button + drawer */}
        <button
          onClick={() => setActionOpen(true)}
          className="lg:hidden fixed bottom-4 right-4 z-30 bg-[#064E2E] text-white rounded-full px-5 py-3 shadow-xl flex items-center gap-2 text-sm font-medium"
          data-testid="workspace-mobile-action-fab"
        >
          <Menu size={16}/> Take Action
        </button>
        {actionOpen && (
          <div className="lg:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setActionOpen(false)} data-testid="workspace-mobile-action-drawer">
            <div className="absolute right-0 top-0 bottom-0 w-[88%] max-w-sm bg-[#F7F4ED] overflow-y-auto p-4" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-lg text-[#071A33]">Take Action</h2>
                <button onClick={() => setActionOpen(false)}><X size={18}/></button>
              </div>
              <ActionPanel ws={ws} onDone={() => { load(); setActionOpen(false); }}/>
            </div>
          </div>
        )}
      </div>

      <div className="px-4 sm:px-6 lg:px-8 mt-6 pb-12 space-y-5">
        <ProgressStepper status={c.status}/>
        <BottomCards ws={ws}/>
      </div>
    </div>
  );
}
